import React from "react";
import { MapPin, Clock, Phone, MessageCircle } from "lucide-react";
import { useApp } from "../AppContext";
import { FlagStripe } from "./FlagAccents";

const Contact = () => {
    const { t, lang, branding } = useApp();
    const whatsappDigits = (branding.whatsapp_number || "").replace(/\D/g, "") || "18763929056";
    const formatted = (() => {
        const d = whatsappDigits;
        if (d.length === 11 && d.startsWith("1")) return `+1 ${d.slice(1, 4)} ${d.slice(4, 7)} ${d.slice(7)}`;
        if (d.length === 10) return `+1 ${d.slice(0, 3)} ${d.slice(3, 6)} ${d.slice(6)}`;
        return `+${d}`;
    })();
    const hours = lang === "es" ? branding.contact_hours_es : branding.contact_hours_en;
    return (
        <section
            id="contact"
            data-testid="contact-section"
            className="relative py-24 sm:py-32 bg-earth-900 text-earth-50 grain"
        >
            {branding.decorations_enabled && (
                <FlagStripe thickness="h-1.5" className="absolute top-0 left-0 right-0" />
            )}
            <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid lg:grid-cols-2 gap-12">
                <div>
                    <span className="inline-flex items-center gap-2 rounded-full bg-terracotta-500/20 border border-terracotta-500/30 px-4 py-1.5 text-xs uppercase tracking-[0.2em] font-bold text-terracotta-300 mb-5">
                        {t.contact.tag}
                    </span>
                    <h2 className="font-serif font-black text-earth-50 text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
                        {t.contact.title}
                    </h2>
                    <p className="mt-5 text-lg text-earth-200 max-w-xl">
                        {t.contact.subtitle}
                    </p>

                    <div className="mt-10 space-y-5">
                        <div className="flex items-start gap-4">
                            <div className="w-11 h-11 rounded-full bg-terracotta-500/20 flex items-center justify-center shrink-0">
                                <MapPin className="w-5 h-5 text-terracotta-300" />
                            </div>
                            <div>
                                <div className="text-xs uppercase tracking-wider text-earth-400 font-semibold mb-0.5">
                                    Location
                                </div>
                                <div className="text-earth-50 font-medium">
                                    {branding.contact_address || branding.contact_city}
                                </div>
                            </div>
                        </div>
                        <div className="flex items-start gap-4">
                            <div className="w-11 h-11 rounded-full bg-terracotta-500/20 flex items-center justify-center shrink-0">
                                <Clock className="w-5 h-5 text-terracotta-300" />
                            </div>
                            <div>
                                <div className="text-xs uppercase tracking-wider text-earth-400 font-semibold mb-0.5">
                                    Hours
                                </div>
                                <div className="text-earth-50 font-medium">
                                    {hours}
                                </div>
                            </div>
                        </div>
                        <div className="flex items-start gap-4">
                            <div className="w-11 h-11 rounded-full bg-terracotta-500/20 flex items-center justify-center shrink-0">
                                <Phone className="w-5 h-5 text-terracotta-300" />
                            </div>
                            <div>
                                <div className="text-xs uppercase tracking-wider text-earth-400 font-semibold mb-0.5">
                                    WhatsApp
                                </div>
                                <a
                                    href={`https://wa.me/${whatsappDigits}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="text-earth-50 font-medium hover:text-terracotta-300"
                                    data-testid="contact-whatsapp-link"
                                >
                                    {formatted}
                                </a>
                            </div>
                        </div>
                    </div>

                    <a
                        href={`https://wa.me/${whatsappDigits}?text=${encodeURIComponent(
                            "Hola! Quiero hacer un pedido / Hi! I want to place an order"
                        )}`}
                        target="_blank"
                        rel="noreferrer"
                        data-testid="contact-whatsapp-cta-btn"
                        className="btn-whatsapp mt-10"
                    >
                        <MessageCircle className="w-5 h-5" />
                        {t.contact.whatsappCta}
                    </a>
                </div>

                <div className="relative">
                    <div className="aspect-square rounded-[40px] overflow-hidden border-4 border-earth-800 shadow-2xl">
                        <img
                            src="https://images.unsplash.com/photo-1761315414620-7f0f3ebdd866?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHw0fHxjYXJpYmJlYW4lMjBmb29kJTIwY2hpY2hhcnJvbiUyMHN0ZXd8ZW58MHx8fHwxNzc4NDUwNTY3fDA&ixlib=rb-4.1.0&q=85"
                            alt="Chicharron"
                            className="w-full h-full object-cover hover:scale-105 transition-transform duration-1000"
                        />
                    </div>
                    <div className="absolute -top-6 -right-6 bg-terracotta-500 text-white rounded-full w-32 h-32 flex flex-col items-center justify-center shadow-2xl font-serif italic text-center leading-tight rotate-12 hover:rotate-0 transition-transform">
                        <span className="text-xs uppercase tracking-wider not-italic font-bold">
                            ¡Pide
                        </span>
                        <span className="text-2xl">Hoy!</span>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Contact;
